import { MAPPING_DRAGGED_ITEM_TYPE, REPORT_PROPERTIES } from '../../../../constants/generalConstants';
import { useDragAndDropState, useMappings } from '../../../../stores/mappingStore';
import {
    MasterDescriptorsMapType,
    ImportTableDescriptorType,
    ImportTableReportType,
    RowIdType,
} from '../../../../types';
import { MASTER_DESCRIPTOR_OVER_PREFIX } from '../../constants';
import ImportDescriptor from '../importDescriptor';
import ImportDescriptorCell from './components/importDescriptorCell';
import MappingReportValue from '../mappingReportValue/mappingReportValue';
import MappingsReportCell from './components/mappingsReportCell';
import MappingsSortableItem from '../mappingsSortableItem';
import MappingsTableRow from './components/mappingsTableRow';
import MasterDescriptor from '../masterDescriptor';
import MasterDescriptorCell from './components/masterDescriptorCell';
import { GripVertical, Hand } from 'lucide-react';
import './styles.css';

type MappingsTablePropsType = {
    handleRemoveImportDescriptor: (rowId: RowIdType) => void;
    reports: ImportTableReportType[];
    handleMasterDescriptorOnDropSwap: () => void;
    handleMasterDescriptorOnDropInsert: () => void;
    handleImportDescriptorOnDropSwap: () => void;
    importDescriptors: ImportTableDescriptorType[];
    masterDescriptorsMap: MasterDescriptorsMapType;
};

function MappingsTable({
    reports,
    handleMasterDescriptorOnDropSwap,
    handleMasterDescriptorOnDropInsert,
    handleImportDescriptorOnDropSwap,
    importDescriptors,
    masterDescriptorsMap,
    handleRemoveImportDescriptor,
}: MappingsTablePropsType) {
    const mappings = useMappings();
    const { draggedItemType } = useDragAndDropState();
    return (
        <table className="extraction-table mappings">
            <thead>
                <tr>
                    <th rowSpan={6}>Model Descriptors</th>
                    <th rowSpan={6}>Image imported descriptors</th>
                </tr>
                {REPORT_PROPERTIES.map((prop) => {
                    return (
                        <tr key={prop}>
                            {reports.map((r, i) => {
                                return <th key={i}>{r[prop]}</th>;
                            })}
                        </tr>
                    );
                })}
            </thead>
            <tbody>
                {mappings.map((mapping) => {
                    const rowId = mapping.rowId;
                    const descriptorId = mapping.descriptorId;
                    const masterDescriptor = masterDescriptorsMap[descriptorId];
                    const importDescriptor = importDescriptors.find((d) => d.id === rowId);
                    return (
                        <MappingsTableRow
                            key={descriptorId}
                            rowId={rowId}
                            masterDescriptorId={masterDescriptor?.id ?? null}
                        >
                            <MasterDescriptorCell masterDescriptor={masterDescriptor}>
                                <MappingsSortableItem
                                    itemType={MAPPING_DRAGGED_ITEM_TYPE.MASTER_DESCRIPTOR}
                                    droppingDisabled={draggedItemType !== MAPPING_DRAGGED_ITEM_TYPE.MASTER_DESCRIPTOR}
                                    strategies={['INSERT']}
                                    draggingDisabled={!!masterDescriptor}
                                    handleOnDropInsert={handleMasterDescriptorOnDropInsert}
                                    id={descriptorId}
                                    overId={`${MASTER_DESCRIPTOR_OVER_PREFIX}${descriptorId}`}
                                >
                                    {masterDescriptor ? (
                                        <MasterDescriptor masterDescriptor={masterDescriptor} />
                                    ) : (
                                        <div className="flex full-height flex-align-center">
                                            <GripVertical className="grip-icon" />
                                        </div>
                                    )}
                                </MappingsSortableItem>
                            </MasterDescriptorCell>
                            <ImportDescriptorCell confidence={mapping.confidence} rowId={rowId}>
                                <MappingsSortableItem
                                    itemType={MAPPING_DRAGGED_ITEM_TYPE.IMPORT_DESCRIPTOR}
                                    droppingDisabled={draggedItemType !== MAPPING_DRAGGED_ITEM_TYPE.IMPORT_DESCRIPTOR}
                                    strategies={['SWAP']}
                                    handleOnDropSwap={handleImportDescriptorOnDropSwap}
                                    id={rowId}
                                    overId={descriptorId}
                                    draggingDisabled={!importDescriptor}
                                >
                                    {!!importDescriptor && (
                                        <ImportDescriptor
                                            mapping={mapping}
                                            importDescriptor={importDescriptor}
                                            handleRemoveImportDescriptor={handleRemoveImportDescriptor}
                                        />
                                    )}
                                </MappingsSortableItem>
                            </ImportDescriptorCell>
                            {reports.map((r, i) => {
                                const fieldValue = r.rowIdToValue[rowId];
                                return (
                                    <MappingsReportCell key={i}>
                                        {!!fieldValue && <MappingReportValue fieldValue={fieldValue} />}
                                    </MappingsReportCell>
                                );
                            })}
                        </MappingsTableRow>
                    );
                })}
            </tbody>
        </table>
    );
}

export default MappingsTable;
